import {dehydrate, QueryClient} from '@tanstack/react-query';
import {GetStaticPathsContext} from 'next';

import {getAllAcademiesForLocaleFromService} from '@udemy/career-academies';
import {disallowSlug, RENDER_MODES} from '@udemy/nextjs-core';

import {enrichAcademyData} from 'src/features/career-academy-landing-page/utils/enrich-academy-data';
import {getCompactTranslations} from 'src/lib-server/locale.server';
import {FOUR_HOURS} from 'src/lib/constants';
import {fetcher} from 'src/lib/gql-fetcher';
import {AppContextData} from 'src/types/page-props';

import {getLayout} from '../../components/layout';
import {
    CareerAcademyOverviewPageFeature,
    CareerAcademyOverviewPageFeatureProps,
} from '../../features/career-academy-overview-page';
import {getStaticAppContext} from '../api/context.api';

export async function getStaticPaths({}: GetStaticPathsContext) {
    return {
        paths: [],
        fallback: 'blocking',
    };
}

export const getStaticProps = disallowSlug(async ({locale}) => {
    locale = locale as string;
    const translations = await getCompactTranslations(locale);
    const client = new QueryClient();

    // Resolve appContext
    const [staticData] = await Promise.all([getStaticAppContext({locale})]);
    const appContext: AppContextData = {
        ...staticData,
        // application specific context can go here
    };

    const academiesData = (
        await getAllAcademiesForLocaleFromService(locale, {
            useEnglishFallback: true,
            customGqlFetcher: fetcher,
            pageOptions: {limit: 100},
        })
    ).filter((academy) => !!academy);

    if (!academiesData?.length) {
        return {
            notFound: true,
        };
    }

    const enrichedAcademiesList = await Promise.all(
        academiesData.map((academy) => enrichAcademyData(academy, locale)),
    );

    const staticProps = {
        props: {
            appContext,
            dehydratedState: dehydrate(client),
            locale,
            pageKey: 'all_careers_page',
            renderMode: RENDER_MODES.ISR, // supports render functions in _document.tsx and _app.tsx.
            translations,
        },
        revalidate: FOUR_HOURS,
    };

    if (enrichedAcademiesList) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (staticProps.props as any).academies = enrichedAcademiesList;
    }

    return staticProps;
});

export default function CareerAcademyOverviewPage(props: CareerAcademyOverviewPageFeatureProps) {
    return <CareerAcademyOverviewPageFeature {...props} />;
}

CareerAcademyOverviewPage.getLayout = getLayout;
